import React, { useEffect, useState } from "react";
import { Plus, BookOpen, Video, Sparkles, Pencil, Trash2, ArrowLeft, Loader2, Eye, EyeOff, PlayCircle } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { getEmbedUrl } from "../lib/video";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const LEVELS = ["Foundation", "Diploma", "Undergraduate", "Postgraduate", "IELTS/Test Prep"];
const AI_KINDS = [{ k: "lesson", l: "Full Lesson" }, { k: "outline", l: "Outline" }, { k: "quiz", l: "Quiz" }, { k: "summary", l: "Summary" }];

export default function TeacherStudio() {
  const { toast } = useToast();
  const [courses, setCourses] = useState([]);
  const [view, setView] = useState("list");
  const [course, setCourse] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [nc, setNc] = useState({ title: "", subject: "", level: "", description: "" });
  const [lessonOpen, setLessonOpen] = useState(false);
  const [lesson, setLesson] = useState({ title: "", video_url: "", content: "", duration: "" });
  const [editingLesson, setEditingLesson] = useState(null);
  const [aiKind, setAiKind] = useState("lesson");
  const [aiLoading, setAiLoading] = useState(false);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizCount, setQuizCount] = useState(5);

  const load = () => api.get("/studio/courses").then((r) => setCourses(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCourse = async (id) => { const { data } = await api.get(`/studio/courses/${id}`); setCourse(data); setView("editor"); };
  const refresh = async () => { if (course) { const { data } = await api.get(`/studio/courses/${course.id}`); setCourse(data); } load(); };

  const createCourse = async () => {
    if (!nc.title) { toast({ title: "Course title required", variant: "destructive" }); return; }
    const { data } = await api.post("/studio/courses", nc);
    setNewOpen(false); setNc({ title: "", subject: "", level: "", description: "" });
    load(); openCourse(data.id);
  };

  const togglePublish = async () => {
    const status = course.status === "Published" ? "Draft" : "Published";
    await api.put(`/studio/courses/${course.id}`, { title: course.title, subject: course.subject, level: course.level, description: course.description, thumbnail: course.thumbnail, status });
    toast({ title: status === "Published" ? "Course published \ud83c\udf89" : "Moved to draft" });
    refresh();
  };

  const delCourse = async () => {
    if (!window.confirm(`Delete course "${course.title}"?`)) return;
    await api.delete(`/studio/courses/${course.id}`); setView("list"); setCourse(null); load();
  };

  const openNewLesson = () => { setEditingLesson(null); setLesson({ title: "", video_url: "", content: "", duration: "", quiz: [] }); setLessonOpen(true); };
  const openEditLesson = (l) => { setEditingLesson(l); setLesson({ title: l.title, video_url: l.video_url || "", content: l.content || "", duration: l.duration || "", quiz: l.quiz || [] }); setLessonOpen(true); };

  const saveLesson = async () => {
    if (!lesson.title) { toast({ title: "Lesson title required", variant: "destructive" }); return; }
    if (editingLesson) await api.put(`/studio/courses/${course.id}/lessons/${editingLesson.id}`, lesson);
    else await api.post(`/studio/courses/${course.id}/lessons`, lesson);
    setLessonOpen(false); refresh(); toast({ title: `Lesson ${editingLesson ? "updated" : "added"}` });
  };

  const delLesson = async (lid) => { if (!window.confirm("Delete this lesson?")) return; await api.delete(`/studio/courses/${course.id}/lessons/${lid}`); refresh(); };

  const generateAI = async () => {
    if (!lesson.title) { toast({ title: "Enter a lesson title/topic first", variant: "destructive" }); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/studio/ai-generate", { topic: lesson.title, kind: aiKind, level: course?.level || "", provider: "gemini", model: "gemini-2.5-flash" });
      setLesson((p) => ({ ...p, content: p.content ? p.content + "\n\n" + data.text : data.text }));
      toast({ title: "AI content added" });
    } catch (e) { toast({ title: "AI generation failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setAiLoading(false); }
  };

  const generateQuiz = async () => {
    if (!lesson.title) { toast({ title: "Enter a lesson title/topic first", variant: "destructive" }); return; }
    setQuizLoading(true);
    try {
      const { data } = await api.post("/studio/ai-quiz", { topic: lesson.title, level: course?.level || "", count: quizCount, provider: "gemini", model: "gemini-2.5-flash" });
      setLesson((p) => ({ ...p, quiz: data.questions }));
      toast({ title: `${data.questions.length} quiz questions generated` });
    } catch (e) { toast({ title: "Quiz generation failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setQuizLoading(false); }
  };

  // ---------- LIST VIEW ----------
  if (view === "list") {
    return (
      <div>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-[#012a1e]">Content Studio</h2>
          <Button onClick={() => setNewOpen(true)} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-5"><Plus size={18} className="mr-1" /> New Course</Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
          {courses.map((c) => (
            <button key={c.id} onClick={() => openCourse(c.id)} className="text-left bg-white rounded-2xl border border-slate-100 p-5 hover-lift">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-[#f0f6f3] grid place-items-center text-[#004030]"><BookOpen size={20} /></div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${c.status === "Published" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{c.status}</span>
              </div>
              <p className="font-display text-lg font-bold text-[#012a1e] mt-3">{c.title}</p>
              <p className="text-slate-500 text-sm">{c.subject || "—"} · {c.level || "—"}</p>
              <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Video size={12} /> {c.lessons_count} lesson(s)</p>
            </button>
          ))}
          {!courses.length && (
            <div className="sm:col-span-2 lg:col-span-3 bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center">
              <p className="text-slate-400">No courses yet. Build your first course with AI assistance.</p>
              <Button onClick={() => setNewOpen(true)} className="mt-3 bg-[#004030] text-white rounded-full px-6"><Plus size={16} className="mr-1" /> New Course</Button>
            </div>
          )}
        </div>

        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display">New Course</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Course Title *</Label><Input value={nc.title} onChange={(e) => setNc({ ...nc, title: e.target.value })} className="h-11" placeholder="e.g. IELTS Speaking Masterclass" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Subject</Label><Input value={nc.subject} onChange={(e) => setNc({ ...nc, subject: e.target.value })} className="h-11" placeholder="e.g. English" /></div>
                <div className="space-y-2"><Label>Level</Label>
                  <Select value={nc.level} onValueChange={(v) => setNc({ ...nc, level: v })}>
                    <SelectTrigger className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Description</Label><Textarea value={nc.description} onChange={(e) => setNc({ ...nc, description: e.target.value })} className="min-h-20" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button><Button onClick={createCourse} className="bg-[#004030] text-white">Create &amp; Build</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---------- EDITOR VIEW ----------
  return (
    <div>
      <button onClick={() => { setView("list"); setCourse(null); }} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#004030] mb-4"><ArrowLeft size={16} /> All courses</button>
      <div className="bg-white rounded-2xl border border-slate-100 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-display text-2xl font-bold text-[#012a1e]">{course.title}</h2>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${course.status === "Published" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{course.status}</span>
          </div>
          <p className="text-slate-500 text-sm mt-1">{course.subject || "—"} · {course.level || "—"} · {(course.lessons || []).length} lessons</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={togglePublish} className={`rounded-full ${course.status === "Published" ? "bg-slate-200 text-slate-700 hover:bg-slate-300" : "bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b]"}`}>
            {course.status === "Published" ? <><EyeOff size={16} className="mr-1" /> Unpublish</> : <><Eye size={16} className="mr-1" /> Publish</>}
          </Button>
          <Button onClick={delCourse} variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"><Trash2 size={16} /></Button>
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        <h3 className="font-semibold text-[#012a1e]">Lessons</h3>
        <Button onClick={openNewLesson} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-5"><Plus size={17} className="mr-1" /> Add Lesson</Button>
      </div>

      <div className="space-y-3 mt-4">
        {(course.lessons || []).map((l, i) => (
          <div key={l.id} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#004030] text-[#e0c05a] grid place-items-center font-bold shrink-0">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#012a1e]">{l.title}</p>
              <p className="text-xs text-slate-400 flex items-center gap-2 mt-0.5">
                {l.video_url && <span className="inline-flex items-center gap-1 text-[#004030]"><PlayCircle size={13} /> Video</span>}
                {l.duration && <span>{l.duration}</span>}
              </p>
              {l.content && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{l.content}</p>}
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => openEditLesson(l)} className="text-slate-500 hover:text-[#004030] p-1.5"><Pencil size={15} /></button>
              <button onClick={() => delLesson(l.id)} className="text-slate-500 hover:text-red-600 p-1.5"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {!(course.lessons || []).length && <p className="text-slate-400 text-sm text-center py-8">No lessons yet. Add your first video lesson.</p>}
      </div>

      {/* Lesson dialog with AI builder */}
      <Dialog open={lessonOpen} onOpenChange={setLessonOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editingLesson ? "Edit" : "Add"} Lesson</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Lesson Title / Topic *</Label><Input value={lesson.title} onChange={(e) => setLesson({ ...lesson, title: e.target.value })} className="h-11" placeholder="e.g. Introduction to Cell Biology" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Video URL (YouTube/Vimeo)</Label><Input value={lesson.video_url} onChange={(e) => setLesson({ ...lesson, video_url: e.target.value })} className="h-11" placeholder="https://youtu.be/..." /></div>
              <div className="space-y-2"><Label>Duration</Label><Input value={lesson.duration} onChange={(e) => setLesson({ ...lesson, duration: e.target.value })} className="h-11" placeholder="e.g. 12 min" /></div>
            </div>
            {lesson.video_url && getEmbedUrl(lesson.video_url) && (
              <div className="aspect-video rounded-lg overflow-hidden border border-slate-200">
                <iframe title="preview" src={getEmbedUrl(lesson.video_url)} className="w-full h-full" allowFullScreen />
              </div>
            )}
            <div className="bg-[#f7faf8] border border-[#c9a227]/30 rounded-xl p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-[#004030] flex items-center gap-1.5"><Sparkles size={16} className="text-[#c9a227]" /> AI Content Builder</span>
                <Select value={aiKind} onValueChange={setAiKind}>
                  <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>{AI_KINDS.map((k) => <SelectItem key={k.k} value={k.k}>{k.l}</SelectItem>)}</SelectContent>
                </Select>
                <Button onClick={generateAI} disabled={aiLoading} className="h-9 bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full px-4 text-sm font-semibold">
                  {aiLoading ? <Loader2 className="animate-spin" size={15} /> : "Generate with AI"}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1.5">Uses the lesson title as the topic. Generated text is added to the content below.</p>
            </div>
            <div className="space-y-2"><Label>Lesson Content</Label><Textarea value={lesson.content} onChange={(e) => setLesson({ ...lesson, content: e.target.value })} className="min-h-52" placeholder="Write or generate lesson content..." /></div>

            {/* Interactive quiz builder */}
            <div className="bg-[#f7faf8] border border-[#004030]/15 rounded-xl p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-[#004030] flex items-center gap-1.5"><Sparkles size={16} className="text-[#c9a227]" /> Interactive Quiz</span>
                <Input type="number" min={3} max={10} value={quizCount} onChange={(e) => setQuizCount(parseInt(e.target.value) || 5)} className="h-9 w-16" />
                <span className="text-xs text-slate-500">questions</span>
                <Button onClick={generateQuiz} disabled={quizLoading} className="h-9 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-4 text-sm font-semibold">
                  {quizLoading ? <Loader2 className="animate-spin" size={15} /> : "Generate Quiz"}
                </Button>
              </div>
              {(lesson.quiz || []).length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-green-700 font-semibold">{lesson.quiz.length} question(s) attached — students take this as a scored test.</p>
                  {lesson.quiz.map((q, qi) => (
                    <div key={qi} className="bg-white rounded-lg border border-slate-100 p-2.5">
                      <p className="text-sm font-medium text-[#012a1e]">{qi + 1}. {q.q}</p>
                      <ul className="mt-1 space-y-0.5">
                        {q.options.map((o, oi) => (
                          <li key={oi} className={`text-xs ${oi === q.answer ? "text-green-700 font-semibold" : "text-slate-500"}`}>{oi === q.answer ? "✓ " : "• "}{o}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLessonOpen(false)}>Cancel</Button><Button onClick={saveLesson} className="bg-[#004030] text-white">Save Lesson</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
